import React, { useCallback, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { useExpanded, useFlexLayout, useRowSelect, useTable } from 'react-table';

import { emptyArray } from 'ui-components/default-props/empty';
import { FulfillmentPlanOrderGridStatus } from '../../../../enums/fulfillmentplan-order-grid-status';
import { useEntitiesFulfillmentPlansFromFulfillmentPlanIds } from '../../../../hooks/entities/selectors/fulfillment-plans';
import { useEntitiesSalesOrderProperty } from '../../../../hooks/entities/selectors/sales-orders';
import { createItemEditableReturnReasonColumnDefinition } from '../../items/items-table/columns/editable-return-reason';
import { createItemImageColumnDefinition, ItemImageColumnId, itemImageDataBuilder } from '../../items/items-table/columns/item-image';
import { createItemNameColumnDefinition, ItemNameColumnId, itemNameDataBuilder } from '../../items/items-table/columns/item-name';
import {
  createItemQuantityColumnDefinition,
  ItemQuantityColumnId,
  itemQuantityDataBuilder,
} from '../../items/items-table/columns/item-quantity';
import { ItemReturnReasonColumnId, itemReturnReasonDataBuilder } from '../../items/items-table/columns/item-return-reason';
import {
  createItemReturnQuantityColumnDefinition,
  ItemReturnQuantityColumnId,
  itemReturnQuantityDataBuilder,
} from '../../items/items-table/columns/return-quantity';
import { createRowSelectionColumnDefinition } from '../../items/items-table/columns/row-selection-cell';
import messages from './messages';

const columns = [
  createRowSelectionColumnDefinition(),
  createItemNameColumnDefinition(),
  createItemImageColumnDefinition(),
  createItemQuantityColumnDefinition({
    Header: <FormattedMessage {...messages.shippedQuantity} />,
  }),
  createItemReturnQuantityColumnDefinition(),
  createItemEditableReturnReasonColumnDefinition(),
];

const getInitialReturnItems = (fulfillmentPlans) => {
  return fulfillmentPlans.reduce((arr, plan) => {
    const { items = [], orderGridStatus } = plan;
    if (orderGridStatus === FulfillmentPlanOrderGridStatus.Shipped.name) {
      const returnItems = items.map((item) => {
        return {
          ...item,
          returnQuantity: item.quantity,
        };
      });
      arr.push(...returnItems);
    }
    return arr;
  }, []);
};

export const useReturnItemsTable = (salesOrderId) => {
  const fulfillmentPlanIds = useEntitiesSalesOrderProperty(salesOrderId, 'fulfillmentPlanIds') || emptyArray;
  const fulfillmentPlans = useEntitiesFulfillmentPlansFromFulfillmentPlanIds(fulfillmentPlanIds);
  const [returnItems, setReturnItems] = useState(() => getInitialReturnItems(fulfillmentPlans));

  const formattedItems = useMemo(
    () =>
      returnItems.map((item) => {
        return {
          [ItemNameColumnId]: itemNameDataBuilder(item),
          [ItemImageColumnId]: itemImageDataBuilder(item),
          [ItemQuantityColumnId]: itemQuantityDataBuilder(item),
          [ItemReturnQuantityColumnId]: itemReturnQuantityDataBuilder(item),
          [ItemReturnReasonColumnId]: itemReturnReasonDataBuilder(item),
          // not shown but needed to actually do something with the items once they are selected
          fulfillmentPlanItemId: item.fulfillmentPlanItemId,
        };
      }),
    [returnItems],
  );

  const updateRowProperty = useCallback((rowId, propertyName, newValue) => {
    setReturnItems((prevState) => {
      const newState = [...prevState];
      newState[rowId][propertyName] = newValue;
      return newState;
    });
  }, []);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow, selectedFlatRows } = useTable(
    {
      columns,
      data: formattedItems,
      autoResetSelectedRows: false,
      autoResetExpanded: false,
      updateRowProperty,
    },
    useFlexLayout,
    useExpanded,
    useRowSelect,
  );

  return useMemo(() => {
    return {
      getTableProps,
      headerGroups,
      getTableBodyProps,
      rows,
      prepareRow,
      selectedItems: selectedFlatRows ?? emptyArray,
    };
  }, [getTableProps, headerGroups, getTableBodyProps, rows, prepareRow, selectedFlatRows]);
};
